/**
 * Phase 3 — CandidateTable
 *
 * Sortable + paginated table of candidates. Each row carries:
 *   index, status pill, pk, pk_gain, design_cost, efficiency,
 *   dominant variable deltas (top 2 inline), run_id link,
 *   actions (Promote).
 *
 * Phase 3.6 additions (additive only):
 *   • Pk column displays "± pk_se" when pk_se > 0.
 *   • Tie-#1 pill alongside the index when ``tie_bucket === 1``.
 *   • New columns: ``recommendation_score`` (sortable) +
 *     ``recommendation_rank`` (small numeric badge).
 *   • Default sort changes from ``pk DESC`` to
 *     ``recommendation_score DESC`` when the controller does not
 *     supply a ``sort`` prop. Existing pk DESC remains available.
 *
 * Server-side pagination via the `limit` / `skip` props passed up.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpDown, ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from "lucide-react";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import StatusPill from "@/components/StatusPill";

const COLUMNS = [
    { key: "candidate_index", label: "#", sortable: true, align: "right" },
    { key: "status", label: "Status", sortable: false, align: "left" },
    { key: "pk", label: "Pk", sortable: true, align: "right" },
    {
        key: "confirmation_pk", label: "Confirmed", sortable: true, align: "right",
    },
    {
        key: "pk_gain", label: "Pk gain", sortable: true, align: "right",
    },
    {
        key: "design_cost", label: "Design cost", sortable: true, align: "right",
    },
    {
        key: "efficiency", label: "Efficiency", sortable: true, align: "right",
    },
    {
        key: "recommendation_score", label: "Score", sortable: true, align: "right",
    },
    {
        key: "recommendation_rank", label: "Rank", sortable: true, align: "right",
    },
    { key: "deltas", label: "Top changes", sortable: false, align: "left" },
    { key: "run_id", label: "Run", sortable: false, align: "left" },
    { key: "actions", label: "", sortable: false, align: "right" },
];

const DEFAULT_SORT = { field: "recommendation_score", dir: "desc" };

function num(v, digits = 2) {
    if (typeof v !== "number" || Number.isNaN(v)) return "—";
    return v.toFixed(digits);
}

function deltaSpan(v, digits = 3) {
    if (typeof v !== "number" || Number.isNaN(v)) return "—";
    const sign = v >= 0 ? "+" : "";
    return (
        <span
            className={
                v >= 0 ? "text-ux-success tabular-nums" : "text-ux-danger tabular-nums"
            }
        >
            {sign}
            {v.toFixed(digits)}
        </span>
    );
}

function pkCell(pk, se) {
    if (typeof pk !== "number" || Number.isNaN(pk)) return "—";
    if (typeof se === "number" && se > 0) {
        return (
            <span className="tabular-nums">
                {pk.toFixed(3)}
                <span className="text-ux-text-3 text-[10px]"> ± {se.toFixed(3)}</span>
            </span>
        );
    }
    return <span className="tabular-nums">{pk.toFixed(3)}</span>;
}

function confirmedCell(c) {
    if (!c.confirmed || typeof c.confirmation_pk !== "number") {
        return <span className="text-ux-text-3">—</span>;
    }
    const pk = c.confirmation_pk;
    const se = c.confirmation_pk_se;
    const diverged = !!c.confirmation_disagreement;
    return (
        <span className="inline-flex items-center gap-1 justify-end">
            <span className="tabular-nums">
                {pk.toFixed(3)}
                {typeof se === "number" && se > 0 && (
                    <span className="text-ux-text-3 text-[10px]"> ± {se.toFixed(3)}</span>
                )}
            </span>
            {diverged && (
                <span
                    className="text-[9px] uppercase tracking-wider bg-ux-warning-soft text-ux-warning px-1 py-[1px] rounded"
                    title="Confirmation Pk fell outside the predicted CI."
                    data-testid={`candidate-diverged-pill-${c.id}`}
                >
                    Diverged
                </span>
            )}
        </span>
    );
}

function topDeltas(deltas, n = 2) {
    if (!deltas || typeof deltas !== "object") return [];
    return Object.entries(deltas)
        .sort(([, a], [, b]) => Math.abs(b) - Math.abs(a))
        .slice(0, n)
        .map(([k, v]) => `${k.split(".").slice(-1)[0]}=${typeof v === "number" ? v.toFixed(2) : String(v)}`);
}

export default function CandidateTable({
    candidates,
    loading = false,
    canPromote = false,
    onPromote,
    page,
    pageSize,
    onPageChange,
    sort,
    onSortChange,
    bestCandidateId,
}) {
    // Phase 3.6 — fall back to default sort when caller hasn't set one.
    const [internalSort, setInternalSort] = useState(DEFAULT_SORT);
    const effectiveSort = sort || internalSort;

    // Tell the parent about the new default once on mount, but only if
    // the parent has provided a setter and is currently on the legacy
    // (pk DESC) default. This is a no-op when the parent is already
    // controlled with a non-default sort.
    useEffect(() => {
        if (
            onSortChange
            && (!sort || (sort.field === "pk" && sort.dir === "desc"))
        ) {
            onSortChange(DEFAULT_SORT);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sorted = useMemo(() => {
        if (!candidates) return [];
        const out = [...candidates];
        if (effectiveSort?.field) {
            out.sort((a, b) => {
                const va = a[effectiveSort.field];
                const vb = b[effectiveSort.field];
                if (va == null && vb == null) return 0;
                if (va == null) return 1;
                if (vb == null) return -1;
                if (typeof va === "number" && typeof vb === "number") {
                    return effectiveSort.dir === "asc" ? va - vb : vb - va;
                }
                return effectiveSort.dir === "asc"
                    ? String(va).localeCompare(String(vb))
                    : String(vb).localeCompare(String(va));
            });
        }
        return out;
    }, [candidates, effectiveSort]);

    const headerClick = (col) => {
        if (!col.sortable) return;
        const dir =
            effectiveSort?.field === col.key && effectiveSort.dir === "desc"
                ? "asc"
                : "desc";
        const next = { field: col.key, dir };
        if (onSortChange) onSortChange(next);
        else setInternalSort(next);
    };

    return (
        <div
            className="rounded-md border border-ux-border-1 bg-ux-bg-1 overflow-hidden"
            data-testid="candidate-table"
        >
            <div className="overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-ux-bg-2">
                            {COLUMNS.map((c) => (
                                <TableHead
                                    key={c.key}
                                    onClick={() => headerClick(c)}
                                    className={[
                                        "text-[10px] uppercase tracking-wider text-ux-text-3",
                                        c.sortable ? "cursor-pointer select-none" : "",
                                        c.align === "right" ? "text-right" : "text-left",
                                    ].join(" ")}
                                    data-testid={`candidate-table-th-${c.key}`}
                                >
                                    <span className="inline-flex items-center gap-1">
                                        {c.label}
                                        {c.sortable && effectiveSort?.field === c.key ? (
                                            effectiveSort.dir === "asc" ? (
                                                <ChevronUp className="w-3 h-3" />
                                            ) : (
                                                <ChevronDown className="w-3 h-3" />
                                            )
                                        ) : c.sortable ? (
                                            <ArrowUpDown className="w-3 h-3 opacity-30" />
                                        ) : null}
                                    </span>
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell
                                    colSpan={COLUMNS.length}
                                    className="py-8 text-center text-ux-text-3 text-[12px]"
                                >
                                    Loading…
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && sorted.length === 0 && (
                            <TableRow>
                                <TableCell
                                    colSpan={COLUMNS.length}
                                    className="py-8 text-center text-ux-text-3 text-[12px]"
                                    data-testid="candidate-table-empty"
                                >
                                    No candidates yet.
                                </TableCell>
                            </TableRow>
                        )}
                        {sorted.map((c) => {
                            const isBest = c.id === bestCandidateId;
                            const isTopTie = c.tie_bucket === 1;
                            return (
                                <TableRow
                                    key={c.id}
                                    className={isBest ? "bg-ux-accent-soft/30" : ""}
                                    data-testid={`candidate-row-${c.id}`}
                                >
                                    <TableCell className="text-right tabular-nums text-[12px]">
                                        <span className="inline-flex items-center gap-1 justify-end">
                                            {c.candidate_index}
                                            {isTopTie && (
                                                <span
                                                    className="text-[9px] uppercase tracking-wider bg-ux-info-soft text-ux-info px-1 py-[1px] rounded"
                                                    title="Statistically tied with the recommended candidate."
                                                    data-testid={`candidate-tie-pill-${c.id}`}
                                                >
                                                    tie #1
                                                </span>
                                            )}
                                            {c.pareto_flag && (
                                                <span
                                                    className="text-[9px] uppercase tracking-wider bg-ux-accent-soft text-ux-accent px-1 py-[1px] rounded"
                                                    title="On the Pareto frontier (non-dominated on design_cost vs Pk)."
                                                    data-testid={`candidate-pareto-pill-${c.id}`}
                                                >
                                                    Pareto
                                                </span>
                                            )}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <StatusPill status={c.status} testId={`candidate-status-${c.id}`} />
                                    </TableCell>
                                    <TableCell
                                        className="text-right text-[12px]"
                                        data-testid={`candidate-pk-${c.id}`}
                                    >
                                        {pkCell(c.pk, c.pk_se)}
                                    </TableCell>
                                    <TableCell
                                        className="text-right text-[12px]"
                                        data-testid={`candidate-confirmed-${c.id}`}
                                    >
                                        {confirmedCell(c)}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums text-[12px]">
                                        {deltaSpan(c.pk_gain, 3)}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums text-[12px]">
                                        {num(c.design_cost, 3)}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums text-[12px]">
                                        {num(c.efficiency, 3)}
                                    </TableCell>
                                    <TableCell
                                        className="text-right tabular-nums text-[12px]"
                                        data-testid={`candidate-score-${c.id}`}
                                    >
                                        {num(c.recommendation_score, 3)}
                                    </TableCell>
                                    <TableCell className="text-right text-[12px]">
                                        {typeof c.recommendation_rank === "number" ? (
                                            <span
                                                className="inline-flex items-center text-[10px] uppercase tracking-wider text-ux-accent bg-ux-accent-soft px-1.5 py-0.5 rounded"
                                                data-testid={`candidate-rank-${c.id}`}
                                            >
                                                {c.recommendation_rank}
                                            </span>
                                        ) : (
                                            <span className="text-ux-text-3">—</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-[11px] font-mono text-ux-text-3 max-w-[260px] truncate">
                                        {topDeltas(c.deltas).join(" · ") || "—"}
                                    </TableCell>
                                    <TableCell className="text-[11px] font-mono">
                                        {c.run_id ? (
                                            <Link
                                                to={`/analyse/runs/${c.run_id}`}
                                                className="text-ux-accent hover:underline"
                                                data-testid={`candidate-run-link-${c.id}`}
                                            >
                                                {c.run_id.slice(0, 12)}…
                                            </Link>
                                        ) : (
                                            <span className="text-ux-text-3">—</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        {canPromote && (c.status === "succeeded" || c.status === "cached") && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => onPromote && onPromote(c.id)}
                                                data-testid={`candidate-promote-${c.id}`}
                                                className="h-7 text-[11px]"
                                            >
                                                Promote
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>

            {(page !== undefined || pageSize !== undefined) && (
                <div className="flex items-center justify-between px-3 py-2 border-t border-ux-border-1 text-[11px] text-ux-text-3">
                    <span>
                        Page {(page ?? 0) + 1} · {pageSize} per page
                    </span>
                    <div className="flex gap-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onPageChange && onPageChange(Math.max(0, (page || 0) - 1))}
                            disabled={(page || 0) === 0}
                            data-testid="candidate-table-prev"
                            className="h-6 px-2"
                        >
                            <ChevronLeft className="w-3 h-3" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onPageChange && onPageChange((page || 0) + 1)}
                            disabled={sorted.length < (pageSize || 0)}
                            data-testid="candidate-table-next"
                            className="h-6 px-2"
                        >
                            <ChevronRight className="w-3 h-3" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
